import React, { useState } from 'react';
import { Form, Input, InputNumber, Row, Col, Button, Card, message, Spin } from 'antd';
import { RightOutlined, EnvironmentOutlined, SearchOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const Step1SiteInfo = ({ onNext, formData, isFirstStep }) => {
    const [form] = Form.useForm();
    const [loadingCep, setLoadingCep] = useState(false);

    const handleSubmit = async (values) => {
        onNext(values);
    };

    // Buscar endereço pelo CEP usando ViaCEP
    const handleCepBlur = async (e) => {
        const cep = e.target.value.replace(/\D/g, ''); // Remove caracteres não numéricos
        
        if (cep.length !== 8) {
            return;
        }

        setLoadingCep(true);
        
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
                message.error('CEP não encontrado');
                setLoadingCep(false);
                return;
            }

            // Preencher os campos automaticamente
            form.setFieldsValue({
                logradouro: data.logradouro || '',
                bairro: data.bairro || '',
                city: data.localidade || '',
                state: data.uf || '',
                cep: data.cep || cep,
            });

            message.success('Endereço encontrado! Buscando coordenadas...');

            // Buscar latitude e longitude usando Nominatim (OpenStreetMap)
            await buscarCoordenadas(data.logradouro, data.localidade, data.uf);
            
            // Focar no campo número
            setTimeout(() => {
                const numeroInput = document.querySelector('input[id="numero"]');
                if (numeroInput) {
                    numeroInput.focus();
                }
            }, 100);

        } catch (error) {
            message.error('Erro ao buscar CEP. Verifique sua conexão.');
            console.error('Erro ao buscar CEP:', error);
        } finally {
            setLoadingCep(false);
        }
    };

    // Buscar coordenadas (latitude/longitude) usando Nominatim
    const buscarCoordenadas = async (logradouro, cidade, estado) => {
        try {
            // Montar query de busca
            const endereco = `${logradouro}, ${cidade}, ${estado}, Brasil`;
            const encodedAddress = encodeURIComponent(endereco);
            
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
                {
                    headers: {
                        'User-Agent': 'SmartGridPIADSE/1.0' // Nominatim requer User-Agent
                    }
                }
            );
            
            const data = await response.json();

            if (data && data.length > 0) {
                const { lat, lon } = data[0];
                
                form.setFieldsValue({
                    latitude: parseFloat(lat),
                    longitude: parseFloat(lon),
                });

                message.success('Coordenadas encontradas automaticamente!', 2);
            } else {
                message.warning('Coordenadas não encontradas. Você pode preenchê-las manualmente.', 3);
            }
        } catch (error) {
            console.error('Erro ao buscar coordenadas:', error);
            // Não mostra erro ao usuário, coordenadas são opcionais
        }
    };

    // Formatar CEP enquanto digita
    const formatCep = (value) => {
        const cleanValue = value.replace(/\D/g, '');
        if (cleanValue.length <= 5) {
            return cleanValue;
        }
        return `${cleanValue.slice(0, 5)}-${cleanValue.slice(5, 8)}`;
    };

    const handleCepChange = (e) => {
        const formatted = formatCep(e.target.value);
        form.setFieldsValue({ cep: formatted });
    };

    // Pré-preencher com dados existentes
    React.useEffect(() => {
        if (formData) {
            form.setFieldsValue(formData);
        }
    }, [formData, form]);

    return (
        <Card title={
            <span>
                <EnvironmentOutlined style={{ marginRight: '8px', color: '#FB8500' }} />
                Informações do Site
            </span>
        }>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={formData}
            >
                <Form.Item
                    label="Nome do Site"
                    name="name"
                    rules={[
                        { required: true, message: 'Nome do site é obrigatório' },
                        { min: 3, message: 'Nome deve ter no mínimo 3 caracteres' }
                    ]}
                    extra="Ex: Sede São Paulo, Filial Campinas, Condomínio Solar Park"
                >
                    <Input 
                        placeholder="Digite o nome do site" 
                        size="large"
                    />
                </Form.Item>

                {/* CEP em destaque no topo */}
                <Card 
                    size="small" 
                    style={{ 
                        marginBottom: '16px', 
                        backgroundColor: '#f0f5ff',
                        borderColor: '#1890ff'
                    }}
                >
                    <Row gutter={16} align="middle">
                        <Col span={24}>
                            <Form.Item
                                label={
                                    <span>
                                        <SearchOutlined style={{ marginRight: '8px' }} />
                                        CEP (digite para buscar o endereço automaticamente)
                                    </span>
                                }
                                name="cep"
                                rules={[
                                    { required: true, message: 'CEP é obrigatório' },
                                    { pattern: /^\d{5}-?\d{3}$/, message: 'CEP inválido' }
                                ]}
                                style={{ marginBottom: 0 }}
                                extra={loadingCep ? <Spin size="small" /> : "Digite o CEP e pressione Tab ou clique fora para buscar"}
                            >
                                <Input 
                                    placeholder="01234-567" 
                                    size="large"
                                    maxLength={9}
                                    onBlur={handleCepBlur}
                                    onChange={handleCepChange}
                                    suffix={loadingCep ? <Spin size="small" /> : null}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Row gutter={16}>
                    <Col span={16}>
                        <Form.Item
                            label="Logradouro"
                            name="logradouro"
                            rules={[{ required: true, message: 'Logradouro é obrigatório' }]}
                        >
                            <Input placeholder="Será preenchido automaticamente pelo CEP" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Número"
                            name="numero"
                            rules={[{ required: true, message: 'Número é obrigatório' }]}
                        >
                            <Input placeholder="123" id="numero" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Bairro"
                            name="bairro"
                            rules={[{ required: true, message: 'Bairro é obrigatório' }]}
                        >
                            <Input placeholder="Será preenchido automaticamente" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Complemento"
                            name="complemento"
                        >
                            <Input placeholder="Apto 101, Bloco A, etc." />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Cidade"
                            name="city"
                            rules={[{ required: true, message: 'Cidade é obrigatória' }]}
                        >
                            <Input placeholder="Será preenchido automaticamente" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Estado"
                            name="state"
                            rules={[{ required: true, message: 'Estado é obrigatório' }]}
                        >
                            <Input placeholder="SP" maxLength={2} style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Latitude"
                            name="latitude"
                            extra="Preenchido automaticamente pelo CEP"
                        >
                            <InputNumber 
                                style={{ width: '100%' }} 
                                step={0.000001} 
                                placeholder="-23.550520"
                                precision={6}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Longitude"
                            name="longitude"
                            extra="Preenchido automaticamente pelo CEP"
                        >
                            <InputNumber 
                                style={{ width: '100%' }} 
                                step={0.000001} 
                                placeholder="-46.633308"
                                precision={6}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label="Observações"
                    name="observacoes"
                >
                    <TextArea 
                        rows={3} 
                        placeholder="Informações adicionais sobre o site (opcional)"
                    />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Button 
                        type="primary" 
                        htmlType="submit"
                        icon={<RightOutlined />}
                        size="large"
                        style={{ backgroundColor: '#FB8500', borderColor: '#FB8500' }}
                    >
                        Próximo: Solar/Inversor
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default Step1SiteInfo;
